// AdjuntosRegistro.jsx — Componente reutilizable para adjuntar archivos a
// una solicitud (modelo Registro). Es un wrapper controlado por el padre:
//
//   <AdjuntosRegistro
//     tipoSolicitud="horasExtras"
//     registroId={id || null}           // null en modo crear
//     adjuntos={formData.adjuntos}
//     onChange={(arr) => setFormData({ ...formData, adjuntos: arr })}
//   />
//
// - Permite múltiples archivos (lista crecible).
// - Validaciones: tipos (pdf, img, doc, xls), tamaño ≤10MB.
// - Sube a Firebase Storage bajo `registros/{tipo}/{YYYY-MM}/{_id|draft-uuid}/<uuid>-<nombre>`.
// - Si `registroId` existe, el padre puede optar por persistir vía
//   PUT /Registro/agregarAdjunto/:id (que mejor hace el ListaSolicitudes al
//   editar). Para el flujo crear, el array queda en estado y se envía dentro
//   del body del POST /Registro/crear.
//
// shape adjunto: { nombre, url, tipo, tamano }

import React, { useRef, useState } from 'react';
import { Button, Form, Spinner, Badge } from 'react-bootstrap';
import { app } from '../../firebase';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';

const TIPOS_PERMITIDOS = [
    'application/pdf',
    'image/jpeg', 'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const EXT_PERMITIDAS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const iconoPorTipo = (nombre = '', tipo = '') => {
    const ext = (nombre.split('.').pop() || '').toLowerCase();
    if (tipo.startsWith('image/') || ['jpg','jpeg','png'].includes(ext)) return 'bi-image';
    if (ext === 'pdf') return 'bi-file-earmark-pdf';
    if (['doc','docx'].includes(ext)) return 'bi-file-earmark-word';
    if (['xls','xlsx'].includes(ext)) return 'bi-file-earmark-excel';
    return 'bi-file-earmark';
};

const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const AdjuntosRegistro = ({
    tipoSolicitud,
    registroId = null,
    adjuntos = [],
    onChange,
    disabled = false,
}) => {
    const inputRef = useRef(null);
    const [subiendo, setSubiendo] = useState(false);
    // Draft id estable para la sesión de carga cuando no hay _id del registro
    // (modo crear). Se guarda en Firebase bajo esa subcarpeta y queda vinculado
    // al registro cuando el backend persiste las urls.
    const [draftId] = useState(() => `draft-${uuidv4()}`);

    const validarArchivo = (file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!EXT_PERMITIDAS.includes(ext) && !TIPOS_PERMITIDOS.includes(file.type)) {
            return `Tipo no permitido: ${file.name}. Permitidos: ${EXT_PERMITIDAS.join(', ')}`;
        }
        if (file.size > MAX_SIZE_BYTES) {
            return `${file.name} excede 10 MB`;
        }
        return null;
    };

    const subirAFirebase = async (file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const mes = moment().format('YYYY-MM');
        const carpeta = registroId || draftId;
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `registros/${tipoSolicitud || 'otro'}/${mes}/${carpeta}/${uuidv4()}-${safeName}`;
        const storageRef = app.storage().ref();
        const ref = storageRef.child(path);
        await ref.put(file);
        const url = await ref.getDownloadURL();
        return {
            nombre: file.name,
            url,
            tipo: file.type || ext,
            tamano: file.size,
        };
    };

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        // Validaciones previas — abortamos todo si alguno falla.
        for (const f of files) {
            const err = validarArchivo(f);
            if (err) {
                Swal.fire({ icon: 'error', title: 'Archivo inválido', text: err });
                if (inputRef.current) inputRef.current.value = '';
                return;
            }
        }

        setSubiendo(true);
        try {
            const nuevos = [];
            for (const f of files) {
                const adj = await subirAFirebase(f);
                nuevos.push(adj);
            }
            onChange?.([...adjuntos, ...nuevos]);
        } catch (err) {
            console.error('[AdjuntosRegistro] fallo subida:', err);
            Swal.fire({ icon: 'error', title: 'Error al subir', text: err?.message || 'Fallo subiendo a Firebase' });
        } finally {
            setSubiendo(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const eliminar = (idx) => {
        const copia = [...adjuntos];
        copia.splice(idx, 1);
        onChange?.(copia);
    };

    return (
        <div className="mb-3">
            <Form.Label className="d-flex align-items-center gap-2">
                <i className="bi bi-paperclip" /> Adjuntos{' '}
                <small className="text-muted">(opcional · PDF/imágenes/Word/Excel · 10MB max)</small>
            </Form.Label>

            <div className="d-flex align-items-center gap-2 mb-2">
                <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled || subiendo}
                >
                    {subiendo ? (
                        <><Spinner size="sm" animation="border" className="me-2" />Subiendo…</>
                    ) : (
                        <><i className="bi bi-upload me-2" />Agregar archivo(s)</>
                    )}
                </Button>
                {adjuntos.length > 0 && (
                    <Badge bg="secondary">{adjuntos.length} adjunto(s)</Badge>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                style={{ display: 'none' }}
                onChange={onFiles}
                disabled={disabled || subiendo}
            />

            {adjuntos.length > 0 && (
                <ul className="list-group list-group-flush border rounded">
                    {adjuntos.map((a, idx) => (
                        <li key={`${a.url}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center py-2">
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                                <i className={`bi ${iconoPorTipo(a.nombre, a.tipo)}`} />
                                <a href={a.url} target="_blank" rel="noreferrer" className="text-truncate" title={a.nombre} style={{ maxWidth: '320px' }}>
                                    {a.nombre}
                                </a>
                                <small className="text-muted ms-2">{formatSize(a.tamano)}</small>
                            </div>
                            {!disabled && (
                                <Button variant="link" size="sm" className="text-danger p-0" onClick={() => eliminar(idx)} title="Quitar">
                                    <i className="bi bi-trash" />
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AdjuntosRegistro;
